// src/layouts/SidebarLayout/Header/Buttons/Notifications/index.js
import {
  alpha,
  Badge,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import { getDateFnsLocale } from 'src/utils/dateLocale';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import {
  socket,
  initializeSocket,
  disconnectSocket,
  getEventClaimId
} from 'src/socket/config';
import { NOTIF_MESSAGE, NOTIF_FEEDBACK } from 'src/socket/events';
import { useTranslation } from 'react-i18next';
import { translateSystemMessage } from 'src/utils/systemMessages';
import {
  fetchAllNotification,
  markAsViewNotification,
  deleteNotification,
  deleteAllReadNotifications
} from 'src/redux/notificationSlice';
import {
  playNotif,
  isSoundEnabled,
  setSoundEnabled as persistSoundEnabled,
  getNotificationPermission,
  requestNotificationPermission
} from 'src/utils/sound';

// ---------- Badge ----------
const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      background-color: ${alpha(theme.palette.error.main, 0.9)};
      color: white;
      min-width: 16px;
      height: 16px;
      padding: 0;
      &::after {
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        border-radius: 50%;
        box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
        content: "";
      }
    }
`
);

// ---------- Anim cloche ----------
const BellButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  '&.ringing::after': {
    content: '""',
    position: 'absolute',
    inset: -6,
    borderRadius: '50%',
    boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.35)}`,
    animation: 'pulse 900ms ease-out 1'
  },
  '&.ringing svg': {
    animation: 'ring 900ms ease-in-out 1'
  },
  '@keyframes ring': {
    '0%': { transform: 'rotate(0)' },
    '15%': { transform: 'rotate(18deg)' },
    '30%': { transform: 'rotate(-14deg)' },
    '45%': { transform: 'rotate(10deg)' },
    '60%': { transform: 'rotate(-6deg)' },
    '75%': { transform: 'rotate(3deg)' },
    '100%': { transform: 'rotate(0)' }
  },
  '@keyframes pulse': {
    '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.35)}` },
    '100%': { boxShadow: `0 0 0 14px ${alpha(theme.palette.success.main, 0)}` }
  }
}));

// Récupère claimId quel que soit le shape
function getClaimIdFromNotif(notif) {
  if (!notif) return null;
  return (
    notif.claimId ||
    notif.claim ||
    notif.claim_id ||
    notif?.data?.claimId ||
    notif?.data?.claim ||
    notif?.meta?.claimId ||
    notif?.payload?.claimId ||
    notif?.action?.claimId ||
    notif?.action?.claim ||
    null
  );
}

function HeaderNotifications() {
  const { t, i18n } = useTranslation();

  const ref = useRef(null);
  const ringTimeoutRef = useRef(null);

  const [isOpen, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(null);
  const [ringing, setRinging] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(isSoundEnabled);
  const [notifPermission, setNotifPermission] = useState(getNotificationPermission);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accessToken = useSelector((state) => state.client?.accessToken);
  const notifications = useSelector(
    (state) => state.notifications.notifications || []
  );
  const unreadCount = notifications.filter((n) => !n.view).length;

  const handleClose = () => setOpen(false);

  // Le son ne dépend pas de la permission navigateur : seule la notification
  // système en a besoin. On la demande ici, sur un geste utilisateur.
  const handleToggleSound = async (event) => {
    const enabled = event.target.checked;
    setSoundEnabled(enabled);
    persistSoundEnabled(enabled);

    if (enabled && getNotificationPermission() === 'default') {
      await requestNotificationPermission();
      setNotifPermission(getNotificationPermission());
    }
  };

  // Source de vérité = backend (GET /notification/receiver). Les événements
  // socket ne servent que de signal pour resynchroniser.
  const refreshNotifications = useMemo(
    () =>
      debounce(() => {
        dispatch(fetchAllNotification());
      }, 300),
    [dispatch]
  );

  const ring = useCallback(() => {
    setRinging(true);
    clearTimeout(ringTimeoutRef.current);
    ringTimeoutRef.current = setTimeout(() => setRinging(false), 1000);
  }, []);

  // Connexion socket liée à la session.
  useEffect(() => {
    if (accessToken) {
      initializeSocket();
    } else {
      disconnectSocket();
    }
  }, [accessToken]);

  useEffect(() => {
    refreshNotifications();

    const handleNotifMessage = (payload) => {
      const claimId = getEventClaimId(payload);
      const text = payload?.message?.message;
      if (isSoundEnabled()) ring();
      playNotif(
        t('notifNewMessageTitle'),
        typeof text === 'string' && text.trim()
          ? text
          : t('notifNewMessageBody'),
        { tag: `cliclaim-claim-${claimId || 'message'}` }
      );
      refreshNotifications();
    };

    const handleNotifFeedback = (payload) => {
      const claimId = getEventClaimId(payload);
      if (isSoundEnabled()) ring();
      playNotif(
        t('notifFeedbackTitle'),
        t('notifFeedbackBody'),
        { tag: `cliclaim-feedback-${claimId || 'claim'}` }
      );
      refreshNotifications();
    };

    // Après une coupure, des événements ont pu être manqués : on resynchronise.
    const handleReconnect = () => refreshNotifications();
    const handleVisibility = () => {
      if (!document.hidden) refreshNotifications();
    };

    socket.on(NOTIF_MESSAGE, handleNotifMessage);
    socket.on(NOTIF_FEEDBACK, handleNotifFeedback);
    socket.on('connect', handleReconnect);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      socket.off(NOTIF_MESSAGE, handleNotifMessage);
      socket.off(NOTIF_FEEDBACK, handleNotifFeedback);
      socket.off('connect', handleReconnect);
      document.removeEventListener('visibilitychange', handleVisibility);
      refreshNotifications.cancel();
      clearTimeout(ringTimeoutRef.current);
    };
  }, [refreshNotifications, ring, t]);

  const handleBellClick = () => {
    setOpen(true);
  };

  const handleNotificationClick = useCallback(
    async (notif) => {
      try {
        await dispatch(markAsViewNotification(notif._id)).unwrap();
        await dispatch(deleteAllReadNotifications()).unwrap();
      } catch (error) {
        // Naviguer quand même en cas d'erreur
      } finally {
        handleClose();
        if (notif.kind === 'message') {
          const claimId = getClaimIdFromNotif(notif);
          navigate(
            claimId
              ? `/dashboards/messenger?claimId=${encodeURIComponent(claimId)}`
              : '/dashboards/messenger'
          );
        } else if (notif.kind === 'Claim') {
          navigate('/management/claims');
        }
      }
    },
    [dispatch, navigate]
  );

  const handleDeleteNotification = useCallback(
    async (e, notif) => {
      e.stopPropagation();
      try {
        await dispatch(deleteNotification(notif._id)).unwrap();
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  return (
    <>
      <Tooltip arrow title={t('Notifications')}>
        <BellButton
          color="primary"
          ref={ref}
          onClick={handleBellClick}
          className={ringing ? 'ringing' : undefined}
        >
          <NotificationsBadge
            badgeContent={unreadCount}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {soundEnabled ? (
              <NotificationsActiveTwoToneIcon />
            ) : (
              <NotificationsOffIcon />
            )}
          </NotificationsBadge>
        </BellButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* ✅ Header avec toggle son */}
        <Box
          sx={{ p: 2 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">{t('Notifications')}</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={soundEnabled}
                onChange={handleToggleSound}
                size="small"
                color="success"
              />
            }
            label={
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🔊 {t('Sound')}
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Box>
        
        <Divider />
        
        {/* ✅ Message d'information SEULEMENT si refusé */}
        {notifPermission === 'denied' && soundEnabled && (
          <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderBottom: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <Typography variant="caption" color="warning.main">
              {t('notifOsBlocked')}
            </Typography>
          </Box>
        )}
        
        <List sx={{ p: 0 }}>
          {notifications.length > 0 ? (
            notifications.map((notif, index) => (
              <ListItem
                button
                onClick={() => handleNotificationClick(notif)}
                key={notif._id || index}
                sx={{
                  p: 2,
                  minWidth: 350,
                  display: { xs: 'block', sm: 'flex' },
                  backgroundColor: !notif.view
                    ? 'rgba(151, 181, 151, 0.138)'
                    : 'none'
                }}
              >
                <Box
                  flex="1"
                  onMouseEnter={() => setIsHovered(index)}
                  onMouseLeave={() => setIsHovered(null)}
                >
                  {isHovered === index && (
                    <Grid
                      container
                      justifyContent="flex-end"
                      marginTop={-1.5}
                      sx={{ marginInlineStart: 1.6 }}
                      marginBottom={-0.5}
                    >
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={(e) => handleDeleteNotification(e, notif)}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Grid>
                  )}

                  <Box display="flex" justifyContent="space-between">
                    <Typography
                      sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                    >
                      {t(`notifKind.${notif?.kind || 'notification'}`, { defaultValue: notif?.kind })}
                      {!notif?.view && (
                        <FiberManualRecordIcon
                          sx={{ fontSize: 12, marginInlineStart: 0.5, color: '#18e68e' }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {formatDistance(
                        new Date(notif?.createdAt || Date.now()),
                        new Date(),
                        { addSuffix: true, locale: getDateFnsLocale(i18n.language) }
                      )}
                    </Typography>
                  </Box>

                  <Typography component="span" variant="body2" color="text.secondary">
                    {translateSystemMessage(notif?.message, t)}
                  </Typography>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem
              sx={{ p: 2, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}
            >
              <Box flex="1">
                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {t('No notifications yet.')}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
